<template>
  <div class="cv" ref="cvRoot">
    <!-- Eyebrow — Conjure & Vote. Hero header sits below. -->
    <div class="cv-page-title">
      <span class="cv-crumb-mark">✦</span>
      <span class="cv-crumb cv-crumb-here">Manifesting Together</span>
    </div>

    <!-- TOP ROW — the avatar gallery and the feedback board side by side. -->
    <div class="cv-top">
    <!-- HERO — coverflow of avatar candidates: "Leave her as is" · "Conjure your
         own" · the conjured candidates. Center card large, neighbours scaled. -->
    <section v-if="avatarPoll" class="cv-hero">
      <h2 class="cv-hero-title">Choose the Face of Mystic AIda for {{ seasonLabel }}</h2>

      <div class="cv-flow">
        <button type="button" class="cv-flow-arrow" :disabled="avCenter <= 0"
                @click="avStep(-1)" aria-label="Previous"><i class="fas fa-chevron-left"></i></button>

        <div class="cv-flow-stage" ref="stageEl">
          <div class="cv-flow-track" :style="{ transform: `translateX(${trackShift}px)` }">
          <div v-for="(s, i) in slides" :key="s.key" class="cv-slide" :class="tier(i)"
               @click="onSlide(s, i)">
            <!-- Conjure card -->
            <template v-if="s.type === 'conjure'">
              <div class="cv-card cv-card-conjure">
                <span class="cv-card-img cv-card-img-conjure"><i class="fas fa-wand-magic-sparkles"></i></span>
                <span class="cv-card-label">Conjure your own</span>
                <span v-if="pricesVisible" class="cv-card-cost"><i class="fas fa-gem"></i> {{ paintCost }}</span>
              </div>
            </template>
            <!-- Candidate / keep card -->
            <template v-else>
              <div class="cv-card" :class="{ chosen: s.option.mine, owned: s.owned }">
                <span v-if="s.owned" class="cv-card-yours">Yours</span>
                <span class="cv-card-img">
                  <img v-if="s.option.image_url" :src="s.option.image_url" :alt="s.option.label" />
                  <span v-else class="cv-card-ph">✦</span>
                </span>
                <span v-if="s.option.is_keep" class="cv-card-label">{{ s.option.label }}</span>
                <span v-if="s.option.mine" class="cv-card-check"><i class="fas fa-check"></i></span>
              </div>
            </template>
          </div>
          </div>
        </div>

        <button type="button" class="cv-flow-arrow" :disabled="avCenter >= slides.length - 1"
                @click="avStep(1)" aria-label="Next"><i class="fas fa-chevron-right"></i></button>
      </div>

      <div class="cv-hero-foot">
        <button type="button" class="cv-gallery-btn" @click="galleryOpen = true">View Voting Gallery</button>
      </div>
    </section>

      <!-- Open feedback & ideas (upvote board) — beside the gallery. -->
      <section v-if="feedbackPoll" class="cv-skill cv-feedback">
        <div class="cv-skill-head">
          <span class="cv-skill-icon"><i class="fas fa-lightbulb"></i></span>
          <div>
            <span class="cv-skill-eyebrow">FEEDBACK</span>
            <h3 class="cv-skill-title">{{ feedbackPoll.title }}</h3>
          </div>
        </div>
        <p class="cv-skill-sub">{{ feedbackPoll.subtitle }}</p>

        <div class="cv-ideas">
          <div v-for="idea in feedbackIdeas" :key="idea.id" class="cv-idea">
            <button type="button" class="cv-idea-up" :class="{ liked: idea.mine }" @click="vote(feedbackPoll, idea)">
              <i class="fas fa-caret-up"></i><span>{{ idea.votes }}</span>
            </button>
            <span class="cv-idea-text">{{ idea.label }}</span>
          </div>
        </div>

        <div class="cv-skill-foot">
          <span class="cv-skill-total">{{ feedbackPoll.options.length }} ideas</span>
          <button type="button" class="cv-link cv-link-on"
                  @click="store.querent_id ? openConcierge() : requestAuth('voice')">
            <i class="fas fa-comment-dots"></i> Talk to Mystic AIda
          </button>
        </div>
      </section>
    </div>

    <!-- Concierge — the SAME floating layer + slide as Conjure: a scrim over the
         still-visible Voice interface, the panel snapped in via cv-slide, large white ×. -->
    <Transition name="cv-slide">
    <div v-if="conciergeOpen" class="cv-overlay" :style="{ left: overlayLeft + 'px' }" @click.self="conciergeOpen = false">
      <div class="cv-form cv-form-chat">
        <button class="cv-form-close" @click="conciergeOpen = false" aria-label="Close">×</button>
        <ConciergeChat @require-login="requestAuth('voice')" @idea-added="load" />
      </div>
    </div>
    </Transition>

    <LoadState
      v-if="!polls.length"
      :loading="pollsLoading" :failed="pollsFailed"
      message="Couldn't reach the Voice board just now."
      loading-label="Gathering the Voice board"
      @retry="load"
    />

    <!-- VOTING CARDS — next tarot / astrology games + new skill. -->
    <div class="cv-skills">
      <section v-for="poll in cardPolls" :key="poll.key" class="cv-skill">
        <div class="cv-skill-head">
          <span class="cv-skill-icon"><i :class="['fas', meta(poll.key).icon]"></i></span>
          <div>
            <span class="cv-skill-eyebrow">{{ meta(poll.key).eyebrow }}</span>
            <h3 class="cv-skill-title">{{ poll.title }}</h3>
          </div>
        </div>
        <p class="cv-skill-sub">{{ poll.subtitle }}</p>

        <div class="cv-polls">
          <button v-for="opt in poll.options" :key="opt.id" type="button"
                  class="cv-poll" :class="{ leading: opt.leading, mine: opt.mine }"
                  @click="vote(poll, opt)">
            <div class="cv-poll-top">
              <span class="cv-poll-label">
                <i v-if="opt.mine" class="fas fa-check cv-poll-check"></i>{{ opt.label }}
              </span>
              <span class="cv-poll-pct">{{ opt.pct }}%</span>
            </div>
            <span class="cv-poll-bar"><span class="cv-poll-fill" :style="{ width: opt.pct + '%' }"></span></span>
          </button>
        </div>

        <div class="cv-skill-foot">
          <span class="cv-skill-total">{{ poll.total }} votes</span>
        </div>
      </section>
    </div>

    <!-- CONJURE — a floating layer over the (still-visible) Voice interface: a
         semi-opaque scrim slides out below the title so nothing up top shifts, and
         the form sits centered on it. Mystic AIda's chat stays the left layer. -->
    <Transition name="cv-slide">
    <div v-if="conjureOpen" class="cv-overlay" :style="{ left: overlayLeft + 'px' }" @click.self="closeConjure">
      <div class="cv-form">
        <button class="cv-form-close" @click="closeConjure" aria-label="Close">×</button>

        <!-- LOCKED STATUS VIEW — your submission is final (others have voted): show its
             standing (votes + supporters) instead of the editing form. -->
        <template v-if="submissionView">
          <h3 class="cv-builder-title">Your submission · {{ seasonLabel }}</h3>
          <p class="cv-sub-locked"><i class="fas fa-lock"></i> Others have voted - this look is locked in. It can't be changed or deleted.</p>
          <div class="cv-sub-img" v-if="submissionView.image_url">
            <img :src="submissionView.image_url" :alt="submissionView.label" />
          </div>
          <div class="cv-sub-stat">
            <span class="cv-sub-votes">{{ submissionView.votes }}</span>
            <span class="cv-sub-votes-label">vote{{ submissionView.votes === 1 ? '' : 's' }} in the circle</span>
          </div>
          <button v-if="submissionView.share" type="button" class="cv-modal-go cv-share-conjure"
                  @click="shareCandidate(submissionView.share, submissionView.label)">
            <i class="fas fa-share-nodes"></i> {{ shareCopied === submissionView.share ? 'Link copied - go rally votes!' : 'Share your conjure · gather votes' }}
          </button>
          <div class="cv-sub-voters">
            <span class="cv-sub-voters-head">{{ submissionView.supporters }} supporter{{ submissionView.supporters === 1 ? '' : 's' }}</span>
            <div class="cv-sub-voters-list">
              <div v-for="(v, i) in submissionView.voters" :key="i" class="cv-sub-voter">
                <span class="cv-sub-voter-av">
                  <img v-if="v.avatar_url" :src="v.avatar_url" :alt="v.nick_name" />
                  <span v-else>{{ (v.nick_name || '?').charAt(0) }}</span>
                </span>
                <span class="cv-sub-voter-name">{{ v.nick_name }}</span>
              </div>
            </div>
            <p v-if="submissionView.supporters > submissionView.voters.length" class="cv-sub-voters-more">
              + {{ submissionView.supporters - submissionView.voters.length }} more
            </p>
          </div>
        </template>

        <!-- EDITING FORM — only when you don't have a locked submission. -->
        <template v-else>
        <h3 class="cv-builder-title">Mystic AIda · {{ seasonLabel }}</h3>

          <div class="cv-tabs">
            <button class="cv-tab" :class="{ on: cMode === 'simple' }" @click="cMode = 'simple'">Simple</button>
            <button class="cv-tab" :class="{ on: cMode === 'advanced' }" @click="cMode = 'advanced'">Advanced</button>
          </div>

          <!-- SIMPLE (default) — just describe it; Mystic AIda helps from the side panel. -->
          <div v-if="cMode === 'simple'" class="cv-fields">
            <span class="cv-field-label">Describe the {{ seasonLabel }} look</span>
            <textarea v-model="describe" class="cv-modal-input" rows="4"
                      placeholder="e.g. a hooded oracle wrapped in star-threaded robes, a glowing sigil in hand, in an autumn temple at dusk…"></textarea>
            <p class="cv-simple-hint">Mystic AIda is in the side panel to help - just tell her what you envision.</p>
          </div>
          <!-- ADVANCED — the power surface: dial the anchors AND refine in words below.
               The dials never overwrite your text; both pair up at paint, and Mystic
               AIda keeps your dial picks when she shapes the text. -->
          <div v-else class="cv-fields">
            <div class="cv-field"><span class="cv-field-label">Regalia</span>
              <AltarMultiSelect :model="f.outfit" :options="OPTS.outfit" placeholder="Choose the regalia…" @update="f.outfit = $event" /></div>
            <div class="cv-field"><span class="cv-field-label">Background</span>
              <AltarMultiSelect :model="f.background" :options="OPTS.background" placeholder="Choose a setting…" @update="f.background = $event.slice(-1)" /></div>
            <div class="cv-field"><span class="cv-field-label">Mood</span>
              <AltarMultiSelect :model="f.mood" :options="OPTS.mood" placeholder="Choose a mood…" @update="f.mood = $event.slice(-1)" /></div>
            <div class="cv-field"><span class="cv-field-label">Art style</span>
              <AltarMultiSelect :model="f.style" :options="OPTS.style" placeholder="Choose a style…" @update="f.style = $event.slice(-1)" /></div>
            <div class="cv-field cv-field-text">
              <span class="cv-field-label">In your words - Mystic AIda keeps your picks above</span>
              <textarea v-model="describe" class="cv-modal-input" rows="3"
                        placeholder="Refine the look, or ask Mystic AIda in the side panel to shape it…"></textarea>
            </div>
          </div>

          <!-- Past prompts — every wording you've painted (Simple or Advanced),
               restorable into the text box. -->
          <div v-if="pastPrompts.length" class="cv-pp">
            <button type="button" class="cv-pp-toggle" @click="showPastPrompts = !showPastPrompts">
              <i class="fas fa-rotate-left"></i> Past prompts ({{ pastPrompts.length }})
            </button>
            <div v-if="showPastPrompts" class="cv-pp-list">
              <button v-for="(p, i) in pastPrompts" :key="i" type="button" class="cv-pp-item"
                      @click="describe = p" :title="p">{{ p }}</button>
            </div>
          </div>

          <div class="cv-conjure-actions">
            <button class="cv-paint-btn" :disabled="painting" @click="paint">
              <i class="fas fa-wand-magic-sparkles"></i> {{ painting ? 'Painting…' : conjurePaintLabel(paintCost, { pricesVisible, format: formatAmount }) }}
            </button>
          </div>
          <p v-if="conjureError" class="cv-modal-error">{{ conjureError }}</p>

          <!-- Drafts — tap to choose for submit, 🔍 to enlarge. -->
          <div v-if="drafts.length" class="cv-drafts">
            <span class="cv-drafts-label">Your drafts · tap to choose</span>
            <div class="cv-drafts-row">
              <button v-for="d in drafts" :key="d.id" type="button" class="cv-draft"
                      :class="{ on: chosenDraft === d.id }" @click="chosenDraft = d.id">
                <img :src="d.image_url" :alt="d.label" />
                <span class="cv-zoom" @click.stop="enlarge(d.image_url, d.label)" title="Enlarge">
                  <i class="fas fa-magnifying-glass-plus"></i></span>
                <span v-if="chosenDraft === d.id" class="cv-draft-check"><i class="fas fa-check"></i></span>
              </button>
            </div>
            <div class="cv-modal-actions">
              <button v-if="canPublish" class="cv-modal-go" :disabled="submitting" @click="submitChosen">
                <span v-if="!submitting"><i class="fas fa-arrow-up-from-bracket"></i> Publish to the circle</span>
                <span v-else>Publishing…</span>
              </button>
              <span v-else-if="chosenDraftObj && chosenDraftObj.submitted" class="cv-published-note">
                <i class="fas fa-check-circle"></i> Published to the circle
              </span>
            </div>
          </div>
        </template>
      </div>
    </div>
    </Transition>

    <!-- VOTE modal — view someone else's candidate large, then vote (single choice). -->
    <div v-if="voteModal" class="cv-modal-overlay" @click.self="voteModal = null">
      <div class="cv-modal cv-vote">
        <button class="cv-modal-close" aria-label="Close" @click="voteModal = null">×</button>
        <div class="cv-vote-img">
          <img v-if="voteModal.image_url" :src="voteModal.image_url" :alt="voteModal.label" />
          <span v-else class="cv-card-ph">✦</span>
        </div>
        <div class="cv-vote-meta">
          <p v-if="voteModal.creator" class="cv-credit">conjured by
            <button class="cv-credit-link" @click="openCreator(voteModal)">{{ voteModal.creator }}</button></p>
          <span v-else class="cv-credit">{{ voteModal.label }}</span>
          <span class="cv-vote-meta-right">
            <span class="cv-vote-stats">{{ voteModal.pct }}% · {{ voteModal.votes }} votes</span>
            <button v-if="store.querent_id && voteModal.image_url" type="button" class="cv-act"
                    :class="{ heart: isFav(voteModal.image_url) }"
                    @click="toggleFav({ url: voteModal.image_url, label: voteModal.label })">
              <i class="fas fa-heart"></i> {{ isFav(voteModal.image_url) ? 'Saved' : 'Save' }}</button>
            <button v-if="voteModal.share" type="button" class="cv-act" @click="shareCandidate(voteModal.share)">
              <i class="fas fa-share-nodes"></i> {{ shareCopied === voteModal.share ? 'Copied!' : 'Share' }}</button>
          </span>
        </div>
        <button class="cv-modal-go cv-vote-btn" :disabled="voteModal.mine" @click="voteFromModal">
          <span v-if="voteModal.mine"><i class="fas fa-check"></i> Your vote</span>
          <span v-else><i class="fas fa-check-to-slot"></i> Vote for this</span>
        </button>
      </div>
    </div>

    <!-- VOTING GALLERY — a live dashboard: the leader as a hero, then the field
         ranked by votes. Tap any image to enlarge (+ heart it). -->
    <div v-if="galleryOpen && avatarPoll" class="cv-modal-overlay" @click.self="galleryOpen = false">
      <div class="cv-modal cv-dash">
        <button class="cv-modal-close" aria-label="Close" @click="galleryOpen = false">×</button>
        <h3 class="cv-modal-title">The Next Mystic AIda · {{ seasonLabel }}</h3>
        <p class="cv-dash-sub">{{ avatarPoll.total }} vote{{ avatarPoll.total === 1 ? '' : 's' }} cast - the circle is shaping the next Mystic AIda.</p>

        <!-- Sort bar -->
        <div class="cv-gal-bar">
          <span class="cv-dash-label">The field</span>
          <div class="cv-gal-sort" role="tablist">
            <button type="button" :class="{ on: gallerySort === 'top' }" @click="gallerySort = 'top'">Top voted</button>
            <button type="button" :class="{ on: gallerySort === 'trending' }" @click="gallerySort = 'trending'">Trending</button>
            <button type="button" :class="{ on: gallerySort === 'newest' }" @click="gallerySort = 'newest'">Newest</button>
          </div>
        </div>

        <!-- One gallery grid: the vote leader is a crowned 2×2 featured tile; the
             rest of the field flows around it (masonry), captions overlaid on each. -->
        <div v-if="leader || galleryField.length" class="cv-gal-grid">
          <!-- Featured: current vote leader -->
          <div v-if="leader" class="cv-gal-img cv-gal-feat" :class="{ mine: leader.mine }"
               @click="enlarge(leader.image_url, leader.label)">
            <img v-if="leader.image_url" :src="leader.image_url" :alt="leader.label" />
            <span v-else class="cv-card-ph">✦</span>
            <button v-if="store.querent_id && leader.image_url" class="cv-fav" :class="{ on: isFav(leader.image_url) }"
                    @click.stop="toggleFav({ url: leader.image_url, label: leader.label })"
                    :title="isFav(leader.image_url) ? 'Saved' : 'Save to your collection'"><i class="fas fa-heart"></i></button>
            <span class="cv-gal-crown"><i class="fas fa-crown"></i> {{ leader.votes > 0 ? 'Leading' : 'Top pick' }}</span>
            <div class="cv-gal-cap">
              <span class="cv-gal-name">
                <i v-if="leader.mine" class="fas fa-check cv-gal-vcheck"></i>
                <span v-if="leader.creator" class="cv-credit-link" @click.stop="openCreator(leader)">{{ leader.creator }}</span>
                <template v-else>{{ leader.label }}</template>
              </span>
              <span class="cv-gal-votes">{{ leader.pct }}% · {{ leader.votes }} vote{{ leader.votes === 1 ? '' : 's' }}</span>
            </div>
          </div>

          <!-- The rest of the field -->
          <div v-for="opt in galleryField" :key="opt.id" class="cv-gal-img" :class="{ mine: opt.mine }"
               @click="enlarge(opt.image_url, opt.label)">
            <img v-if="opt.image_url" :src="opt.image_url" :alt="opt.label" />
            <span v-else class="cv-card-ph">✦</span>
            <button v-if="store.querent_id && opt.image_url" class="cv-fav sm" :class="{ on: isFav(opt.image_url) }"
                    @click.stop="toggleFav({ url: opt.image_url, label: opt.label })"
                    :title="isFav(opt.image_url) ? 'Saved' : 'Save to your collection'"><i class="fas fa-heart"></i></button>
            <span v-if="gallerySort === 'trending' && opt.recent_votes > 0" class="cv-gal-flame">
              <i class="fas fa-fire"></i> {{ opt.recent_votes }}</span>
            <div class="cv-gal-cap">
              <span class="cv-gal-name">
                <i v-if="opt.mine" class="fas fa-check cv-gal-vcheck"></i>
                <span v-if="opt.creator" class="cv-credit-link" @click.stop="openCreator(opt)">{{ opt.creator }}</span>
                <template v-else>{{ opt.label }}</template>
              </span>
              <span class="cv-gal-votes">{{ opt.pct }}% · {{ opt.votes }}</span>
            </div>
          </div>
        </div>
        <p v-else class="cv-gal-empty">No looks yet - be the first to conjure one.</p>
      </div>
    </div>

    <!-- LIGHTBOX — enlarge any candidate / draft image. -->
    <div v-if="lightbox" class="cv-lightbox" @click="lightbox = null">
      <figure class="cv-lightbox-figure" @click.stop>
        <img :src="lightbox.url" :alt="lightbox.label" class="cv-lightbox-img" />
        <!-- Controls live BELOW the image so the picture stands on its own. -->
        <figcaption class="cv-lightbox-bar">
          <span class="cv-lightbox-cap">{{ lightbox.label }}</span>
          <span class="cv-lightbox-acts">
            <button v-if="store.querent_id" class="cv-lb-btn" :class="{ heart: isFav(lightbox.url) }"
                    @click="toggleFav(lightbox)" :title="isFav(lightbox.url) ? 'Saved' : 'Save to your collection'">
              <i class="fas fa-heart"></i></button>
            <button class="cv-lb-btn" aria-label="Close" @click="lightbox = null"><i class="fas fa-times"></i></button>
          </span>
        </figcaption>
      </figure>
    </div>
  </div>
</template>

<script setup>
/**
 * CommunityVoice — the "Voice" tab feedback loop (Founder Lee's page). Data-driven
 * off /community/polls/: every board is a poll → options → votes. The image-style
 * poll renders as the avatar hero; single text polls as cards; the upvote poll as
 * the feedback board. Submissions ("Conjure"/"Share an idea") are Phase 2.
 */
import { ref, reactive, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { requestAuth } from '@/utils/request-auth'
import { upcomingSeason } from '@/utils/upcoming-season'
import { formatAmount } from '@/utils/carats'
import { conjureLaterWords, conjurePaintLabel } from '@/utils/paint-cost'
import { useRouter } from 'vue-router'
import { store, static_store } from '@/store'
import { candidateShareUrl } from '@/utils/share-links'
import LoadState from '@/components/ui/LoadState.vue'
import { getCommunityPolls, castCommunityVote, generateCandidate, submitCandidate,
  getMyCandidates, getMySubmission, saveAltarSpace, getImageGallery, toggleFavoriteImage } from '@/api-service'
import AltarMultiSelect from '@/components/ui/AltarMultiSelect.vue'
import { trackShare } from '@/utils/analytics'
import ConciergeChat from '@/components/ui/ConciergeChat.vue'

const emit = defineEmits(['require-login', 'need-emeralds', 'reward-earned'])
// A shared candidate link (?candidate=<short>) opens the Voting Gallery on this look.
const props = defineProps({ focusCandidate: { type: String, default: '' } })

// ── Share a candidate (recruit votes off-platform) ──────────────────────────
const shareCopied = ref('')
async function shareCandidate(short) {
  if (!short) return
  // The server's /c/ page (its own preview tags), never the app's address.
  const url = candidateShareUrl(short, static_store)
  const done = () => {
    shareCopied.value = short; setTimeout(() => { if (shareCopied.value === short) shareCopied.value = '' }, 2200)
    trackShare('copy_link', 'candidate', short)
  }
  try { await navigator.clipboard.writeText(url); done() } catch (e) {
    const ta = document.createElement('textarea')
    ta.value = url; ta.style.position = 'fixed'; ta.style.opacity = '0'
    document.body.appendChild(ta); ta.select()
    try { document.execCommand('copy'); done() } catch (_) { /* no-op */ }
    document.body.removeChild(ta)
  }
}

// The conjure overlay is fixed to the viewport but must start at the LEFT edge of the
// content column (= the right edge of the chat window), accounting for the app's
// centered layout margins. Measure that edge and keep it in sync as the chat slides
// open (the content column narrows) and on resize.
const cvRoot = ref(null)
const overlayLeft = ref(0)
function measureOverlay() {
  // Prefer the floating chat's right edge (it overlays the content, out of flow);
  // fall back to the content column's left edge when there's no floating chat.
  const chat = document.querySelector('.ai-zone-float')
  if (chat) {
    const r = chat.getBoundingClientRect()
    if (r.width > 0) { overlayLeft.value = Math.round(r.right); return }
  }
  if (cvRoot.value) overlayLeft.value = Math.max(0, Math.round(cvRoot.value.getBoundingClientRect().left))
}
let _cvResizeObserver = null
let _measureRaf = 0

// Priced by the server. A constant here said 5 while the charge was 10 — a
// number typed into the UI cannot track a price that lives in the registry.
const paintCost = ref(0)
// Conjures are free before the Community opens: no price shows (quest journey API v1.2).
const pricesVisible = computed(() => store.prices_visible !== false)
// The UPCOMING season the circle is imagining — date-driven (northern hemisphere),
// e.g. "Winter 2026" while it's still fall. Every label on the page reads it,
// the hero title included.
const seasonLabel = computed(() => upcomingSeason())
const polls = ref([])
const avatarPoll = computed(() => polls.value.find(p => p.option_style === 'image') || null)
// Voting Gallery dashboard — the field ranked by votes (the keep card + every
// conjured candidate), leader first.
const rankedCandidates = computed(() => {
  if (!avatarPoll.value) return []
  return [...(avatarPoll.value.options || [])]
    .filter(o => o.image_url || o.is_keep)
    .sort((a, b) => (b.votes - a.votes) || (b.pct - a.pct))
})
const leader = computed(() => rankedCandidates.value[0] || null)
// The gallery field = everything below the crowned (top-voted) leader, re-sorted by
// the chosen lens. 'top' keeps the vote order; 'trending' = recent vote velocity
// (last 48h, from the BE); 'newest' = most recently conjured.
const gallerySort = ref('top')   // 'top' | 'trending' | 'newest'
const galleryField = computed(() => {
  const arr = rankedCandidates.value.slice(1)
  if (gallerySort.value === 'newest') {
    return [...arr].sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')) || (b.id - a.id))
  }
  if (gallerySort.value === 'trending') {
    return [...arr].sort((a, b) => ((b.recent_votes || 0) - (a.recent_votes || 0)) || (b.votes - a.votes))
  }
  return arr   // 'top' — already vote-ranked
})
const feedbackPoll = computed(() => polls.value.find(p => p.vote_mode === 'upvote') || null)
// "Best ideas rise to the top": the BE returns options in id/sort_order, so rank
// them here — votes desc, then most-recent, then id — to honor the board's promise.
const feedbackIdeas = computed(() => {
  const p = feedbackPoll.value
  if (!p) return []
  return [...p.options].sort((a, b) =>
    (b.votes - a.votes)
    || String(b.created_at || '').localeCompare(String(a.created_at || ''))
    || (b.id - a.id))
})
const cardPolls = computed(() =>
  polls.value.filter(p => p.vote_mode === 'single' && p.option_style === 'text'
    && p.key !== 'next_astrology_game'))

// ── Avatar coverflow ───────────────────────────────────────────────────────
// Slides: "Leave her as is" (keep) → "Conjure your own" → conjured candidates.
const avCenter = ref(0)
const slides = computed(() => {
  if (!avatarPoll.value) return []
  const opts = avatarPoll.value.options || []
  const keep = opts.filter(o => o.is_keep)
  // Your own submission replaces the "Conjure your own" card; once you've submitted,
  // the CTA is gone and your design leads, others to the right.
  const mine = opts.find(o => o.owned && !o.is_keep)
  const others = opts.filter(o => !o.is_keep && !o.owned)
  const list = keep.map(o => ({ type: 'option', option: o, key: 'o' + o.id }))
  if (mine) list.push({ type: 'option', option: mine, key: 'o' + mine.id, owned: true })
  else if (avatarPoll.value.allow_submissions) list.push({ type: 'conjure', key: 'conjure' })
  others.forEach(o => list.push({ type: 'option', option: o, key: 'o' + o.id }))
  return list
})
// Open centered on the SECOND tile (your design / "Conjure your own") so the
// "Leave her as is" Mystic AIda card sits fully visible to its left.
let _avInit = false
watch(slides, (s) => {
  if (!_avInit && s.length > 1) { avCenter.value = 1; _avInit = true; return }
  if (avCenter.value > s.length - 1) avCenter.value = Math.max(0, s.length - 1)
})

// Slide the whole row so the ACTIVE card lands dead-center of the stage — that way
// the neighbours (incl. the Mystic AIda keep card) are never clipped, whatever the
// center index. Widths mirror the .cv-slide tiers (center 286 / near 152 / far 96).
const stageEl = ref(null)
const stageW = ref(0)
function measureStage() { if (stageEl.value) stageW.value = stageEl.value.clientWidth }
const trackShift = computed(() => {
  if (!stageW.value) return 0
  const GAP = 10
  let before = 0
  if (avCenter.value - 2 >= 0) before += 96 + GAP    // far-left card
  if (avCenter.value - 1 >= 0) before += 152 + GAP   // near-left card
  return Math.round(stageW.value / 2 - before - 286 / 2)
})
watch([avatarPoll, slides], () => nextTick(measureStage))
function tier(i) {
  const d = Math.abs(i - avCenter.value)
  return d === 0 ? 'center' : d === 1 ? 'near' : d === 2 ? 'far' : 'hidden'
}
function avStep(dir) {
  avCenter.value = Math.max(0, Math.min(slides.value.length - 1, avCenter.value + dir))
}
function onSlide(s, i) {
  if (i !== avCenter.value) { avCenter.value = i; return }   // recenter a neighbour
  if (s.type === 'conjure') openConjure()
  else if (s.owned) openConjure(s.option.id, s.option.locked)   // your own → editor, or locked status
  else voteModal.value = s.option              // someone else's → view + vote modal
}

// ── Conjure builder (seasonal embodiment) ───────────────────────────────────
// Simple = describe it + Mystic AIda helps (the default). Advanced = dial each
// facet. Facets are GENDER-NEUTRAL and mystic — the embodiment is open, only the
// essence is fixed. Iterate free; submit one = your vote.
const OPTS = {
  // "Regalia" — mystic vesture that reads on any body/gender.
  outfit: ['Hooded ceremonial robes', 'Layered cloak and cowl', 'Embroidered tunic and sash',
           'Veiled headdress and mantle', 'Star-threaded vestments', 'Astral armor',
           'Beaded ritual garb', 'Flowing oracle mantle'],
  background: ['Sacred grove', 'Starlit expanse', 'Snowbound peaks', 'Candlelit temple',
               'Autumn woodland', 'Cosmic void', 'Ancient observatory', 'Moonlit shore'],
  mood: ['warm and radiant', 'serene and grounded', 'cosmic and vast', 'dreamlike and ethereal',
         'shadowed and mysterious', 'still and frosted'],
  style: ['Illustrated', 'Painterly', 'Watercolor', 'Stained glass', 'Storybook', 'Woodcut',
          'Ethereal', 'Realistic'],
}
const conjureOpen = ref(false)
// Re-measure the form's left anchor across the chat's slide-open animation (~350ms)
// so the form tracks the floating chat's right edge as it expands.
watch(conjureOpen, (open) => {
  cancelAnimationFrame(_measureRaf)
  if (!open) return
  let start = 0
  const tick = (t) => {
    measureOverlay()
    if (!start) start = t
    if (t - start < 460) _measureRaf = requestAnimationFrame(tick)
  }
  _measureRaf = requestAnimationFrame(tick)
})
const cMode = ref('simple')
const f = reactive({ outfit: [], background: [], mood: [], style: [] })
const describe = ref('')
const painting = ref(false)
const submitting = ref(false)
const conjureError = ref('')
const drafts = ref([])
const chosenDraft = ref(0)
// When you open your OWN submission and it's locked (someone else has voted), the
// builder becomes a read-only status view: your image + vote count + supporters.
const submissionView = ref(null)
// The chosen draft + whether it's already the one published to the circle. Publish
// only makes sense when you've picked a DIFFERENT look than what's already live.
const chosenDraftObj = computed(() => drafts.value.find(d => d.id === chosenDraft.value) || null)
const canPublish = computed(() => !!chosenDraftObj.value && !chosenDraftObj.value.submitted)

// The seasonal assistant (Mystic AIda in the left ai-zone) reuses the Altar
// assistant machinery. She shapes the text box in BOTH modes, so she's present the
// whole time the builder is open — and never on the Voice landing.
function fireAssistant(open) {
  window.dispatchEvent(new CustomEvent('altar-open-assistant',
    open ? { detail: { open: true, scope: 'mystic_season' } } : { detail: { open: false } }))
}

async function openConjure(restoreId, locked) {
  if (!store.querent_id) { requestAuth('voice'); return }
  submissionView.value = null
  conjureError.value = ''; cMode.value = 'simple'; describe.value = ''
  f.outfit = []; f.background = []; f.mood = []; f.style = []
  drafts.value = []; chosenDraft.value = 0
  conjureOpen.value = true
  // Locked submission → status view, no editing, no assistant.
  if (locked) {
    fireAssistant(false)
    submissionView.value = await getMySubmission(store.session_id)
    return
  }
  fireAssistant(true)    // Mystic AIda is here to help from the start
  await loadDrafts()     // the archive: your past looks, restorable
  // Opening on your own (unlocked) submission preloads its recipe so you can keep editing.
  if (restoreId) {
    const d = drafts.value.find(x => x.id === restoreId)
    if (d) restoreDraft(d)
  }
}
function closeConjure() {
  if (painting.value || submitting.value) return
  conjureOpen.value = false
  submissionView.value = null
}
// Whatever closes the builder — Back, Submit, or a login redirect — also dismisses
// Mystic AIda. She must never linger on the Voice landing.
watch(conjureOpen, (open) => { if (!open) fireAssistant(false) })

// Load this seeker's past drafts (the archive) so any look can be rolled back to.
async function loadDrafts() {
  const list = await getMyCandidates(store.session_id)
  // newest first from the API; show oldest→newest in the strip
  drafts.value = list.slice().reverse()
}

// Roll back to a past look: restore its dial picks + words. The dials never jolt
// the text — restoring just refills both inputs from the archived recipe.
function restoreDraft(d) {
  const fa = d.facets || {}
  f.outfit = fa.outfit ? String(fa.outfit).split(', ').filter(Boolean) : []
  f.background = fa.background ? [fa.background] : []
  f.mood = fa.mood ? [fa.mood] : []
  f.style = fa.style ? [fa.style] : []
  describe.value = fa.prompt || ''
  cMode.value = (f.outfit.length || f.background.length || f.mood.length || f.style.length)
    ? 'advanced' : 'simple'
  chosenDraft.value = d.id
}

// Her assistant reshapes the description — fold the revision back into the text box
// (mirrors the Altar avatar builder). Mode is left as-is.
function onPromptSuggested(e) {
  const d = (e && e.detail) || {}
  if (d.subject === 'mystic_season' && d.prompt) describe.value = d.prompt
}
onMounted(() => {
  window.addEventListener('altar-prompt-suggested', onPromptSuggested)
  window.addEventListener('resize', measureOverlay)
  window.addEventListener('resize', measureStage)
  measureStage()
  // The content column narrows as the chat slides open → re-measure the left anchor.
  if (window.ResizeObserver && cvRoot.value) {
    _cvResizeObserver = new ResizeObserver(measureOverlay)
    _cvResizeObserver.observe(cvRoot.value)
  }
  measureOverlay()
})
onUnmounted(() => {
  window.removeEventListener('altar-prompt-suggested', onPromptSuggested)
  window.removeEventListener('resize', measureOverlay)
  window.removeEventListener('resize', measureStage)
  if (_cvResizeObserver) { _cvResizeObserver.disconnect(); _cvResizeObserver = null }
  cancelAnimationFrame(_measureRaf)
  fireAssistant(false)   // never leave her open after leaving the Voice tab
})

function buildFacets() {
  return {
    outfit: f.outfit.join(', '),
    background: f.background[0] || '', mood: f.mood[0] || '', style: f.style[0] || '',
    prompt: describe.value.trim(),   // dials + words both flow into the prompt
  }
}

// Keep the mystic_season altar in sync (debounced) so Mystic AIda's assistant is
// ANCHOR-AWARE — she sees your dial picks + current text and never contradicts them.
let _syncTimer = null
watch([f, describe], () => {
  if (!conjureOpen.value || !store.querent_id) return
  clearTimeout(_syncTimer)
  _syncTimer = setTimeout(() => {
    const fa = buildFacets()
    saveAltarSpace(store.session_id, {
      outfit: fa.outfit, background: fa.background, mood: fa.mood, style: fa.style,
      compiled_prompt: fa.prompt,
    }, 'mystic_season').catch(() => {})
  }, 600)
}, { deep: true })
async function paint() {
  if (painting.value) return
  painting.value = true; conjureError.value = ''
  try {
    const r = await generateCandidate(buildFacets(), store.session_id)
    if (r && r.draft) { drafts.value.push(r.draft); chosenDraft.value = r.draft.id }
    if (r && typeof r.balance === 'number') store.querent_carat_balance = r.balance
  } catch (e) {
    const st = e && e.response && e.response.status
    if (st === 401) { conjureOpen.value = false; requestAuth('voice') }
    else if (st === 402) { conjureError.value = `Not enough carats - ${formatAmount(paintCost.value)} to paint.`; emit('need-emeralds') }
    else if (st === 429) conjureError.value = 'Plenty of drafts already - publish one first.'
    else if (conjureLaterWords(e)) conjureError.value = conjureLaterWords(e)
    else conjureError.value = 'The painting faltered. Please try again.'
  } finally {
    painting.value = false
  }
}
async function submitChosen() {
  if (submitting.value || !chosenDraft.value) return
  submitting.value = true; conjureError.value = ''
  try {
    const r = await submitCandidate(chosenDraft.value, store.session_id)
    if (r && r.poll) {
      const i = polls.value.findIndex(p => p.key === r.poll.key)
      if (i >= 0) polls.value[i] = r.poll
      if (typeof r.balance === 'number') store.querent_carat_balance = r.balance
      const idx = slides.value.findIndex(s => s.type === 'option' && s.option.id === r.option_id)
      if (idx >= 0) avCenter.value = idx
    }
    conjureOpen.value = false
  } catch (e) {
    const st = e && e.response && e.response.status
    if (st === 401) { conjureOpen.value = false; requestAuth('voice') }
    else if (st === 409) conjureError.value = 'Your submission is locked - others have voted, so it can\'t be changed.'
    else conjureError.value = 'Could not publish. Please try again.'
  } finally {
    submitting.value = false
  }
}

const galleryOpen = ref(false)
const conciergeOpen = ref(false)
function openConcierge() {
  conciergeOpen.value = true
  nextTick(measureOverlay)   // float to the right of the content, like Conjure
}

// Open the creator's profile (global SeekerModal, z-index 9000 — layers cleanly
// above this gallery/vote modal at 1500; closing it returns here).
function openCreator(opt) {
  if (opt && opt.creator) window.dispatchEvent(new CustomEvent('open-seeker-modal', { detail: { nickName: opt.creator } }))
}

// View + vote modal for someone else's candidate.
const voteModal = ref(null)
// Arriving via a shared candidate link → open that look's view/vote modal ONCE,
// then drop ?candidate= so re-entering Voice (or closing it) doesn't re-pop it.
const router = useRouter()
function focusFromLink() {
  const short = props.focusCandidate
  if (!short || !avatarPoll.value) return
  const opt = (avatarPoll.value.options || []).find(o => o.share === short)
  if (opt) voteModal.value = opt
  const q = { ...router.currentRoute.value.query }
  delete q.candidate
  router.replace({ query: q }).catch(() => {})
}
watch(() => props.focusCandidate, focusFromLink)
async function voteFromModal() {
  const opt = voteModal.value
  if (!opt) return
  await vote(avatarPoll.value, opt)
  voteModal.value = null
}

// Past prompts — the distinct wordings you've painted (Simple or Advanced), newest
// first, restorable into the text box. Derived from the draft archive (no extra
// storage) so it always reflects what you've actually conjured.
const showPastPrompts = ref(false)
const pastPrompts = computed(() => {
  const seen = new Set(); const out = []
  for (const d of drafts.value.slice().reverse()) {
    const p = ((d.facets && d.facets.prompt) || '').trim()
    if (p && !seen.has(p)) { seen.add(p); out.push(p) }
  }
  return out
})

// Click-to-enlarge: any candidate/draft image opens in a full lightbox.
const lightbox = ref(null)
function enlarge(url, label) { if (url) lightbox.value = { url, label } }

// ── Favorites (heart) — reuses the app-wide QuerentFavoriteImage store, tagged
// source 'community_avatar'. Hearted looks appear in the Mirror's Conjuring tab.
const favoriteUrls = ref([])
function isFav(url) { return favoriteUrls.value.includes(url) }
async function loadFavorites() {
  if (!store.querent_id) return
  const g = await getImageGallery(store.session_id)
  favoriteUrls.value = (g && g.favorites ? g.favorites : []).map(f => f.url)
}
let _favBusy = false
async function toggleFav(lb) {
  if (_favBusy || !lb || !lb.url || !store.querent_id) return
  _favBusy = true
  try {
    const r = await toggleFavoriteImage(store.session_id, lb.url,
      { source: 'community_avatar', title: lb.label || '' })
    if (r && r.ok) {
      if (r.favorited) { if (!isFav(lb.url)) favoriteUrls.value.push(lb.url) }
      else favoriteUrls.value = favoriteUrls.value.filter(u => u !== lb.url)
    }
  } catch (e) { /* leave state as-is on failure */ } finally { _favBusy = false }
}

// Per-board icon + eyebrow (the API stays presentation-agnostic).
const META = {
  next_tarot_game: { icon: 'fa-layer-group', eyebrow: 'TAROT' },
  next_astrology_game: { icon: 'fa-star', eyebrow: 'ASTROLOGY' },
  next_skill: { icon: 'fa-hat-wizard', eyebrow: 'NEW SKILL' },
}
function meta(key) { return META[key] || { icon: 'fa-circle-dot', eyebrow: '' } }

const pollsLoading = ref(false)
const pollsFailed = ref(false)
async function load() {
  pollsLoading.value = true
  pollsFailed.value = false
  const d = await getCommunityPolls(store.session_id)
  pollsFailed.value = !!d?.failed
  pollsLoading.value = false
  if (d?.failed) return   // keep whatever is already on the board
  polls.value = d.polls || []
  // The board carries the price so the button can never quote a stale one.
  if (typeof d?.paint_cost === 'number') paintCost.value = d.paint_cost
}
onMounted(() => { load().then(focusFromLink); loadFavorites() })

let voting = false
async function vote(poll, option) {
  if (voting || !poll || poll.status !== 'open') return
  // Pre-gate rather than letting the request 401. The vote is replayed after
  // login, so the tap is not thrown away.
  if (!store.querent_id) {
    requestAuth('voice', { action: 'vote-poll', args: { pollKey: poll.key, optionId: option.id } })
    return
  }
  voting = true
  try {
    const r = await castCommunityVote(poll.key, option.id, store.session_id)
    if (r && r.poll) {
      const i = polls.value.findIndex(p => p.key === r.poll.key)
      if (i >= 0) polls.value[i] = r.poll
    }
    if (r && r.reward_available) emit('reward-earned')   // 10 ✦ now waiting in Quest → badge jiggles
  } catch (e) {
    if (e && e.response && e.response.status === 401) {
      requestAuth('voice', { action: 'vote-poll', args: { pollKey: poll.key, optionId: option.id } })
    }
  } finally {
    voting = false
  }
}
</script>

<style scoped>
/* Side gutter + centred measure. This matched .covenant's outer padding (0
   horizontal) but never got the inner .covenant-section treatment, so every
   card in the Voice view ran to the viewport edge on mobile while Fireside sat
   comfortably inset. Same 16px gutter as .covenant-section and the tab bar
   above it, so the three community views line up. */
.cv { color: #E8D8FF; padding: 4px 16px 24px; max-width: 1024px; margin: 0 auto; }
/* Daily-vote reward banner + locked note */
/* Centered page header, outside the hero box. */
/* Match the Fireside .discover-featured-label eyebrow: Cinzel, gold, uppercase, letter-spaced. */
.cv-page-title { font-family: 'Cinzel', serif; font-size: 11px; font-weight: 500; letter-spacing: 0.2em;
  text-transform: uppercase; color: #FFDFA3; line-height: 1; text-align: left; margin: 4px 0 14px;
  display: flex; align-items: center; flex-wrap: wrap; }
.cv-crumb-mark { margin-right: 8px; }
.cv-crumb-sep { opacity: 0.4; margin: 0 7px; }
.cv-crumb-here { color: #ffe9bf; }

/* Top row — avatar gallery + feedback board side by side. */
.cv-top { display: flex; gap: 16px; align-items: stretch; margin-bottom: 22px; }
.cv-top .cv-hero { flex: 1 1 62%; min-width: 0; margin-bottom: 0; }
.cv-top .cv-feedback { flex: 1 1 34%; min-width: 0; margin: 0; }
@media (max-width: 760px) { .cv-top { flex-direction: column; } }

/* Hero */
.cv-hero { border: 1px solid rgba(176,134,238,0.32); border-radius: 18px; padding: 22px 22px 20px;
  background: linear-gradient(165deg, rgba(60,40,104,0.4), rgba(24,12,46,0.5)); margin-bottom: 22px; }
.cv-eyebrow { font-size: 10.5px; letter-spacing: 0.16em; text-transform: uppercase; color: #bcd0ff; }
.cv-hero-title { font-family: 'Cinzel', serif; font-size: 24px; font-weight: 500; font-style: italic;
  color: #FFDFA3; line-height: 1.2; margin: 0 0 18px; text-align: center; }
.cv-hero-sub { font-size: 13.5px; line-height: 1.6; color: rgba(232,216,255,0.72); margin: 0 0 18px; max-width: 620px; }

/* Avatar coverflow — center card large; neighbours scaled + dimmed; arrows flank. */
.cv-flow { display: flex; align-items: center; gap: 6px; }
/* Fixed height so the centered card enlarging (and varying label/badge heights)
   never reflows the hero — the row stays constant and "View Gallery" doesn't jump. */
.cv-flow-stage { flex: 1; min-width: 0; height: 344px; position: relative; overflow: hidden; box-sizing: border-box; }
/* The track holds all slides left-to-right; it's translated so the active card
   lands dead-center of the stage (neighbours never clip). */
.cv-flow-track { position: absolute; top: 0; left: 0; height: 100%; display: flex; align-items: center; gap: 10px;
  transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1); will-change: transform; }
.cv-flow-arrow { flex: none; width: 34px; height: 34px; border-radius: 50%; cursor: pointer;
  background: rgba(20,8,40,0.6); border: 1px solid rgba(195,169,255,0.4); color: #c9b8ec; font-size: 13px;
  display: flex; align-items: center; justify-content: center; transition: background 0.15s, color 0.15s, opacity 0.15s; }
.cv-flow-arrow:hover:not(:disabled) { background: rgba(102,51,153,0.6); color: #f0d9a3; }
.cv-flow-arrow:disabled { opacity: 0.25; cursor: default; }
.cv-slide { flex: none; cursor: pointer; transition: width 0.25s ease, opacity 0.25s ease, transform 0.25s ease; }
.cv-slide.center { width: 286px; opacity: 1; z-index: 3; }
.cv-slide.near { width: 152px; opacity: 0.62; }
.cv-slide.far { width: 96px; opacity: 0.32; }
.cv-slide.hidden { display: none; }

/* Image-forward card: the look fills it, only a thin frame around the picture. */
.cv-card { position: relative; display: flex; flex-direction: column; align-items: center; gap: 8px;
  background: rgba(20,8,40,0.6); border: 1px solid rgba(155,134,201,0.32); border-radius: 16px;
  padding: 8px; }
.cv-slide.center .cv-card { border-color: rgba(176,134,238,0.55); box-shadow: 0 14px 38px -16px rgba(0,0,0,0.85); }
.cv-card.chosen { border-color: rgba(240,217,163,0.8); background: rgba(102,51,153,0.35); }
.cv-card.owned { border-color: rgba(240,217,163,0.6); }
.cv-card-yours { position: absolute; top: 8px; left: 8px; z-index: 4; font-size: 9px; letter-spacing: 0.06em;
  text-transform: uppercase; color: #1a0f2e; background: #C9A94E; border-radius: 999px; padding: 2px 8px; }
.cv-card-img { width: 100%; aspect-ratio: 1; border-radius: 12px; overflow: hidden; background: #150a28;
  border: 1px solid rgba(240,217,163,0.22); display: flex; align-items: center; justify-content: center; }
.cv-card-img img { width: 100%; height: 100%; object-fit: cover; display: block; }
.cv-card-ph { font-size: 32px; color: rgba(201,169,78,0.45); }
.cv-card-label { font-family: 'Cinzel', serif; font-size: 13px; color: #f5e8c0; text-align: center; line-height: 1.2; }
.cv-card-bar { width: 100%; height: 5px; border-radius: 999px; background: rgba(255,255,255,0.08); overflow: hidden; }
.cv-card-fill { display: block; height: 100%; border-radius: 999px; background: linear-gradient(90deg,#8a5fd6,#FFDFA3); }
.cv-card-votes { font-size: 11px; color: rgba(195,169,255,0.7); }
.cv-card-check { position: absolute; top: 8px; right: 8px; width: 20px; height: 20px; border-radius: 50%;
  background: #C9A94E; color: #1a0f2e; font-size: 10px; display: flex; align-items: center; justify-content: center; }
/* Conjure card */
.cv-card-conjure { justify-content: center; min-height: 100%; }
.cv-card-img-conjure { color: #c3a9ff; font-size: 30px; border-style: dashed; }
.cv-card-cost { display: inline-flex; align-items: center; gap: 5px; font-size: 12px; color: #f0d9a3;
  background: rgba(201,169,78,0.18); border: 1px solid rgba(201,169,78,0.4); border-radius: 999px; padding: 3px 11px; }

.cv-hero-foot { margin-top: 8px; text-align: center; }
.cv-gallery-btn { font-family: 'Cinzel', serif; font-size: 12px; letter-spacing: 0.06em; color: #d8b87a;
  background: none; border: none; padding: 2px 0; cursor: pointer;
  text-decoration: underline; text-underline-offset: 4px; text-decoration-color: rgba(216,184,122,0.4);
  transition: color 0.15s, text-decoration-color 0.15s; }
.cv-gallery-btn:hover { color: #fff7e6; text-decoration-color: rgba(255,247,230,0.7); }

/* Zoom affordance on any image (drafts, gallery tiles, center card). */
.cv-zoom { position: absolute; bottom: 6px; right: 6px; width: 24px; height: 24px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center; cursor: pointer; z-index: 4;
  background: rgba(12,6,26,0.72); border: 1px solid rgba(240,217,163,0.4); color: #f0d9a3; font-size: 11px;
  opacity: 0; transition: opacity 0.15s, background 0.15s; }
.cv-draft:hover .cv-zoom, .cv-gtile:hover .cv-zoom, .cv-slide.center:hover .cv-zoom { opacity: 1; }
.cv-zoom:hover { background: rgba(102,51,153,0.85); color: #fff7e6; }
/* Image containers need positioning context for the zoom button. */
.cv-card-img, .cv-gtile-img { position: relative; }

/* Lightbox — full-screen enlarged image. */
.cv-lightbox { position: fixed; inset: 0; z-index: 1600; background: rgba(0,0,0,0.88);
  display: flex; align-items: center; justify-content: center; padding: 5vh 4vw; cursor: zoom-out; }
.cv-lightbox-figure { position: relative; margin: 0; max-width: 92vw; max-height: 90vh; cursor: default; text-align: center; }
.cv-lightbox-img { max-width: 92vw; max-height: 82vh; border-radius: 14px; display: block;
  box-shadow: 0 30px 80px -20px rgba(0,0,0,0.9); border: 1px solid rgba(240,217,163,0.25); }
/* Controls below the image — the picture is never covered. */
.cv-lightbox-bar { display: flex; align-items: center; justify-content: center; gap: 14px; margin-top: 14px; }
.cv-lightbox-cap { font-family: 'Cinzel', serif; font-size: 14px; color: #f5e8c0; }
.cv-lightbox-acts { display: inline-flex; align-items: center; gap: 10px; }
.cv-lb-btn { width: 36px; height: 36px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
  cursor: pointer; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.18);
  color: rgba(255,255,255,0.7); font-size: 15px; transition: background 0.15s, color 0.15s, transform 0.12s; }
.cv-lb-btn:hover { background: rgba(102,51,153,0.7); color: #fff7e6; transform: scale(1.06); }
.cv-lb-btn.heart { color: #ff6b9d; border-color: rgba(255,107,157,0.6); }
/* Heart — save the enlarged look to your collection (Mirror → Conjuring). */
/* Top-LEFT so it never crowds the × close button (top-right). */
.cv-fav { position: absolute; top: 12px; left: 12px; width: 42px; height: 42px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center; cursor: pointer; z-index: 2;
  background: rgba(12,6,26,0.7); border: 1px solid rgba(240,217,163,0.35); color: rgba(255,255,255,0.7);
  font-size: 17px; transition: color 0.15s, background 0.15s, transform 0.12s; }
.cv-fav:hover { background: rgba(102,51,153,0.7); transform: scale(1.06); }
.cv-fav.on { color: #ff6b9d; border-color: rgba(255,107,157,0.6); }

/* Conjure + gallery modals */
.cv-modal-overlay { position: fixed; inset: 0; z-index: 1500; background: rgba(0,0,0,0.78);
  display: flex; align-items: flex-start; justify-content: center; padding: 8vh 16px 16px; overflow-y: auto; }
.cv-modal { position: relative; width: 100%; max-width: 460px; box-sizing: border-box; background: #14071f;
  border: 1px solid rgba(240,217,163,0.22); border-radius: 18px; padding: 22px; color: #e9dcff;
  box-shadow: 0 26px 60px -16px rgba(0,0,0,0.85); }
/* Solid circle so the × stays visible over bright candidate images. */
.cv-modal-close { position: absolute; top: 12px; right: 12px; z-index: 5; width: 34px; height: 34px;
  display: flex; align-items: center; justify-content: center; border-radius: 50%; border: 1px solid rgba(240,217,163,0.35);
  background: rgba(12,6,26,0.72); color: #fff; font-size: 22px; line-height: 1; cursor: pointer;
  transition: background 0.15s, transform 0.12s; }
.cv-modal-close:hover { background: rgba(102,51,153,0.85); color: #fff7e6; transform: scale(1.06); }
/* Reserve the close button's lane. It's absolutely positioned at right:12px
   with a 34px box, so it reaches 24px into this 22px-padded content column and
   sat on top of the title once the title grew long enough to fill the row
   ("The Next Mystic AIda · Fall 2026"). */
.cv-modal-title { font-family: 'Cinzel', serif; font-size: 19px; color: #f5e8c0; margin: 0 0 6px;
  padding-right: 40px; }
.cv-modal-sub { font-size: 12.5px; color: rgba(232,216,255,0.7); margin: 0 0 14px; line-height: 1.5; }
.cv-modal-input { width: 100%; box-sizing: border-box; background: rgba(255,255,255,0.04);
  border: 1px solid rgba(195,169,255,0.22); border-radius: 10px; color: #E8D8FF; font-size: 13.5px;
  padding: 10px 12px; outline: none; resize: vertical; font-family: inherit; }
.cv-modal-input:focus { border-color: rgba(102,51,153,0.6); }
.cv-modal-error { font-size: 12px; color: #f0a3a3; margin: 8px 0 0; }
.cv-modal-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 14px; }
.cv-modal-ghost { background: none; border: 1px solid rgba(155,134,201,0.4); color: #9b86c9; border-radius: 9px;
  padding: 9px 16px; cursor: pointer; font-family: inherit; font-size: 13px; }
.cv-modal-go { font-family: 'Cinzel', serif; font-size: 13px; color: #1a0f2e;
  background: linear-gradient(180deg,#FFDFA3,#cdae6a); border: none; border-radius: 9px; padding: 9px 18px; cursor: pointer; }
.cv-modal-go:disabled, .cv-modal-ghost:disabled { opacity: 0.5; cursor: default; }
.cv-published-note { display: inline-flex; align-items: center; gap: 7px; font-size: 12.5px;
  color: rgba(180,230,190,0.85); font-family: 'Cinzel', serif; letter-spacing: 0.02em; }
.cv-published-note i { color: #7fd49a; }

/* Seasonal builder as a FLOATING layer: a semi-opaque scrim over the (still-rendered)
   Voice interface, with the form centered on it. Nothing in the layout reflows — the
   scrim just overlays, so the top menu stays put. The left ai-zone (Mystic AIda chat)
   is given a higher z-index while open so it overlaps this scrim and stays usable. */
/* `left` is set inline to the measured left edge of the content column (i.e. the
   right edge of the chat window) so the form snaps beside the chat, accounting for
   the app's centered layout margins. */
.cv-overlay {
  position: fixed; top: 0; right: 0; bottom: 0; z-index: 45;
  display: flex; align-items: center; justify-content: flex-start;
  padding: 24px 16px; overflow-y: auto;
  background: linear-gradient(180deg, rgba(8,3,18,0.55), rgba(8,3,18,0.82));
  backdrop-filter: blur(2px);
}
.cv-form-close { position: absolute; top: 10px; right: 14px; z-index: 2; background: none; border: none;
  cursor: pointer; color: #fff; font-size: 30px; line-height: 1; padding: 4px; opacity: 0.85;
  transition: opacity 0.15s, transform 0.15s; }
.cv-form-close:hover { opacity: 1; transform: scale(1.08); }
.cv-form { position: relative; width: 100%; max-width: 560px; margin: 0; box-sizing: border-box; max-height: calc(100vh - 110px);
  overflow-y: auto; border: 1px solid rgba(155,134,201,0.4); border-radius: 16px;
  padding: 20px; background: linear-gradient(180deg, rgba(40,26,68,0.96), rgba(22,12,42,0.96));
  box-shadow: 0 30px 80px -20px rgba(0,0,0,0.85); }
/* Slide out below the title (down into place) + fade — eased and unhurried. */
/* Concierge chat reuses .cv-form chrome but is a fixed-height flex column so the
   message log scrolls inside it (rather than growing the panel). */
.cv-form-chat { display: flex; flex-direction: column; height: min(78vh, 680px); overflow: hidden; }
.cv-slide-enter-active, .cv-slide-leave-active { transition: opacity 0.4s ease; }
.cv-slide-enter-from, .cv-slide-leave-to { opacity: 0; }
.cv-slide-enter-active .cv-form, .cv-slide-leave-active .cv-form { transition: transform 0.55s cubic-bezier(0.2,0,0.2,1), opacity 0.45s ease; }
.cv-slide-enter-from .cv-form, .cv-slide-leave-to .cv-form { transform: translateY(-30px); opacity: 0; }
.cv-builder-title { font-family: 'Cinzel', serif; font-size: 19px; color: #f5e8c0; margin: 0 0 4px; text-align: center; }

/* Locked submission status view */
.cv-sub-locked { display: flex; align-items: center; gap: 7px; font-size: 12px; color: #f0d9a3;
  background: rgba(201,169,78,0.12); border: 1px solid rgba(201,169,78,0.32); border-radius: 9px;
  padding: 8px 11px; margin: 6px 0 14px; }
.cv-sub-img { width: 100%; max-width: 280px; margin: 0 auto 14px; aspect-ratio: 1; border-radius: 12px;
  overflow: hidden; border: 1px solid rgba(240,217,163,0.25); background: #150a28; }
.cv-sub-img img { width: 100%; height: 100%; object-fit: cover; display: block; }
.cv-sub-stat { display: flex; align-items: baseline; justify-content: center; gap: 8px; margin-bottom: 16px; }
.cv-sub-votes { font-family: 'Cinzel', serif; font-size: 30px; color: #FFDFA3; line-height: 1; }
.cv-sub-votes-label { font-size: 12.5px; color: rgba(195,169,255,0.75); }
.cv-sub-voters { border-top: 1px solid rgba(255,255,255,0.08); padding-top: 12px; }
.cv-sub-voters-head { font-size: 10.5px; letter-spacing: 0.08em; text-transform: uppercase;
  color: rgba(195,169,255,0.7); display: block; margin-bottom: 10px; }
.cv-sub-voters-list { display: flex; flex-direction: column; gap: 8px; max-height: 240px; overflow-y: auto; }
.cv-sub-voter { display: flex; align-items: center; gap: 10px; }
.cv-sub-voter-av { width: 30px; height: 30px; border-radius: 50%; overflow: hidden; flex: none; background: rgba(102,51,153,0.3);
  display: flex; align-items: center; justify-content: center; color: #c9b8ec; font-size: 12px; font-weight: 700; }
.cv-sub-voter-av img { width: 100%; height: 100%; object-fit: cover; display: block; }
.cv-sub-voter-name { font-size: 13px; color: #e9dcff; }
.cv-sub-voters-more { font-size: 11.5px; color: rgba(195,169,255,0.6); font-style: italic; margin: 10px 0 0; }
.cv-builder-sub { font-size: 12.5px; line-height: 1.5; color: rgba(232,216,255,0.7); margin: 0 0 14px; }
.cv-tabs { display: inline-flex; gap: 3px; padding: 3px; border-radius: 10px; margin: 4px 0 14px;
  background: rgba(20,8,40,0.5); border: 1px solid rgba(155,134,201,0.24); }
.cv-tab { border: none; background: none; cursor: pointer; font-family: 'Cinzel', serif; font-size: 12px;
  color: rgba(232,216,255,0.6); padding: 6px 16px; border-radius: 8px; }
.cv-tab.on { background: rgba(102,51,153,0.6); color: #fff7e6; }
.cv-fields { display: flex; flex-direction: column; gap: 12px; }
.cv-simple-hint { font-size: 11.5px; color: rgba(195,169,255,0.65); font-style: italic; margin: 8px 0 0; }
/* The "in your words" box sits under the dials in Advanced, gently separated. */
.cv-field-text { margin-top: 4px; padding-top: 12px; border-top: 1px solid rgba(255,255,255,0.07); }
.cv-field { display: flex; flex-direction: column; gap: 6px; }
.cv-field-label { font-size: 11px; letter-spacing: 0.06em; text-transform: uppercase; color: rgba(195,169,255,0.7); }
/* Past prompts — restorable wordings. */
.cv-pp { margin-top: 14px; }
.cv-pp-toggle { background: none; border: none; cursor: pointer; font-family: inherit; font-size: 12px;
  color: rgba(195,169,255,0.8); padding: 0; display: inline-flex; align-items: center; gap: 6px; }
.cv-pp-toggle:hover { color: #C3A9FF; }
.cv-pp-list { display: flex; flex-direction: column; gap: 6px; margin-top: 8px; }
.cv-pp-item { text-align: left; background: rgba(20,8,40,0.5); border: 1px solid rgba(155,134,201,0.24);
  border-radius: 9px; color: rgba(232,216,255,0.82); font-family: inherit; font-size: 12px; line-height: 1.4;
  padding: 8px 10px; cursor: pointer; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.cv-pp-item:hover { border-color: rgba(240,217,163,0.5); color: #f5e8c0; }

.cv-conjure-actions { margin-top: 14px; }
.cv-paint-btn { display: inline-flex; align-items: center; gap: 8px; font-family: 'Cinzel', serif; font-size: 13px;
  color: #fff; background: linear-gradient(180deg,#7b4bb0,#4e2776); border: none; border-radius: 10px;
  padding: 10px 20px; cursor: pointer; }
.cv-paint-btn:disabled { opacity: 0.6; cursor: default; }
.cv-drafts { margin-top: 16px; padding-top: 12px; border-top: 1px solid rgba(255,255,255,0.08); }
.cv-drafts-label { font-size: 10.5px; letter-spacing: 0.08em; text-transform: uppercase; color: rgba(195,169,255,0.6); }
.cv-drafts-row { display: flex; gap: 10px; overflow-x: auto; margin-top: 9px; padding-bottom: 2px; }
.cv-draft { position: relative; flex: 0 0 84px; width: 84px; height: 84px; border-radius: 12px; overflow: hidden;
  cursor: pointer; padding: 0; background: #150a28; border: 2px solid rgba(155,134,201,0.3); }
.cv-draft img { width: 100%; height: 100%; object-fit: cover; display: block; }
.cv-draft.on { border-color: #f0d9a3; }
.cv-draft-check { position: absolute; top: 4px; right: 4px; width: 18px; height: 18px; border-radius: 50%;
  background: #C9A94E; color: #1a0f2e; font-size: 9px; display: flex; align-items: center; justify-content: center; }
/* View + vote modal — big image-forward view of a candidate. Extra top padding
   gives the × its own header band so it never sits on the image. */
.cv-vote { max-width: 640px; text-align: center; padding-top: 52px; }
.cv-vote-img { position: relative; width: 100%; max-height: 76vh; border-radius: 12px; overflow: hidden;
  background: #150a28; border: 1px solid rgba(240,217,163,0.22); display: flex; align-items: center;
  justify-content: center; margin-bottom: 14px; }
.cv-vote-img img { width: 100%; height: auto; max-height: 76vh; object-fit: contain; display: block; }
.cv-vote-stats { font-size: 12.5px; color: rgba(195,169,255,0.75); margin: 4px 0 16px; }
/* "conjured by" creator credit + profile link */
.cv-credit { font-size: 13px; color: rgba(232,216,255,0.7); margin: 0 0 4px; }
.cv-credit-link { background: none; border: none; padding: 0; cursor: pointer; font-family: 'Cinzel', serif;
  font-size: 14px; color: #FFDFA3; text-decoration: underline; text-underline-offset: 2px; }
.cv-credit-link:hover { color: #fff7e6; }
.cv-credit-link.lg { font-size: 17px; }
.cv-vote-btn { width: 100%; display: inline-flex; align-items: center; justify-content: center; gap: 8px; padding: 11px; }
.cv-vote-note { font-size: 11.5px; color: rgba(195,169,255,0.6); font-style: italic; margin: 10px 0 0; }
/* One meta line: "conjured by …" on the left; stats + Save + Share on the right. */
.cv-vote-meta { display: flex; align-items: center; justify-content: space-between; gap: 12px;
  flex-wrap: wrap; margin: 4px 0 14px; text-align: left; }
.cv-vote-meta .cv-credit { margin: 0; }
.cv-vote-meta .cv-vote-stats { margin: 0; }
.cv-vote-meta-right { display: inline-flex; align-items: center; gap: 16px; }
.cv-act { display: inline-flex; align-items: center; gap: 6px; background: none; border: none;
  color: rgba(195,169,255,0.72); font-family: inherit; font-size: 11.5px; cursor: pointer; transition: color 0.15s; }
.cv-act:hover { color: #C3A9FF; }
.cv-act.heart { color: #ff6b9d; }
.cv-share-conjure { width: 100%; display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  margin: 0 0 16px; padding: 11px; }

/* Voting Gallery dashboard */
.cv-dash { max-width: 880px; }
.cv-dash-sub { font-size: 12.5px; color: rgba(232,216,255,0.7); margin: 2px 0 16px; line-height: 1.5; }
/* Sort bar */
.cv-dash-label { font-size: 10.5px; letter-spacing: 0.1em; text-transform: uppercase; color: rgba(255,255,255,0.45); margin: 0; }
.cv-gal-bar { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin: 4px 0 14px; }
.cv-gal-sort { display: inline-flex; gap: 3px; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.07);
  border-radius: 999px; padding: 3px; }
.cv-gal-sort button { font-family: 'Cinzel', serif; font-size: 11px; letter-spacing: 0.03em; color: rgba(232,216,255,0.6);
  background: none; border: none; border-radius: 999px; padding: 5px 13px; cursor: pointer; transition: background 0.15s, color 0.15s; }
.cv-gal-sort button.on { background: rgba(102,51,153,0.55); color: #f5e8c0; }
.cv-gal-sort button:hover:not(.on) { color: #e9dcff; }

/* Gallery grid — uniform square tiles; leader is a crowned 2×2 feature. */
.cv-gal-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  grid-auto-flow: dense; gap: 12px; }
.cv-gal-img { position: relative; aspect-ratio: 1; border-radius: 14px; overflow: hidden; background: #150a28;
  border: 1px solid rgba(176,134,238,0.25); cursor: zoom-in; }
.cv-gal-img img { width: 100%; height: 100%; object-fit: cover; display: block; transition: transform 0.3s ease; }
.cv-gal-img:hover img { transform: scale(1.05); }
.cv-gal-img.mine { border-color: rgba(240,217,163,0.7); box-shadow: 0 0 0 1px rgba(240,217,163,0.4); }
.cv-gal-feat { grid-column: span 2; grid-row: span 2; border-color: rgba(240,217,163,0.5); }
.cv-fav.sm { top: 8px; left: 8px; width: 32px; height: 32px; font-size: 13px; }
/* Featured tile: crown sits top-left, so move its heart to the right. */
.cv-gal-feat .cv-fav { left: auto; right: 12px; }
.cv-gal-crown { position: absolute; top: 10px; left: 10px; z-index: 2; display: inline-flex; align-items: center; gap: 6px;
  font-size: 10px; letter-spacing: 0.06em; text-transform: uppercase; font-weight: 700; color: #1a0f2e;
  background: #C9A94E; border-radius: 999px; padding: 4px 11px; }
.cv-gal-flame { position: absolute; top: 8px; right: 8px; display: inline-flex; align-items: center; gap: 4px;
  font-size: 11px; font-weight: 700; color: #ffd9a3; background: rgba(20,8,40,0.78);
  border: 1px solid rgba(255,140,60,0.5); border-radius: 999px; padding: 3px 8px; }
.cv-gal-flame i { color: #ff8c3c; }
/* Caption overlaid on the image bottom — keeps every tile a clean square. */
.cv-gal-cap { position: absolute; left: 0; right: 0; bottom: 0; display: flex; align-items: flex-end;
  justify-content: space-between; gap: 8px; padding: 22px 10px 9px; pointer-events: none;
  background: linear-gradient(to top, rgba(8,4,18,0.94), rgba(8,4,18,0.5) 60%, transparent); }
.cv-gal-cap .cv-credit-link { pointer-events: auto; }
.cv-gal-name { font-size: 12px; color: #f3e9ff; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.cv-gal-vcheck { color: #f0d9a3; font-size: 10px; margin-right: 5px; }
.cv-gal-votes { flex: none; font-size: 11px; font-weight: 600; color: #FFDFA3; }
.cv-gal-feat .cv-gal-cap { padding: 30px 16px 14px; }
.cv-gal-feat .cv-gal-name { font-family: 'Cinzel', serif; font-size: 17px; }
.cv-gal-feat .cv-gal-votes { font-size: 13px; }
.cv-gal-empty { font-size: 12.5px; color: rgba(232,216,255,0.55); font-style: italic; text-align: center; padding: 18px 0; }

/* Skill voting cards */
.cv-skills { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 16px; }
.cv-skill { border: 1px solid rgba(155,134,201,0.3); border-radius: 16px; padding: 18px;
  background: linear-gradient(180deg, rgba(40,26,68,0.4), rgba(22,12,42,0.4)); }
.cv-skill-head { display: flex; align-items: center; gap: 12px; }
.cv-skill-icon { width: 38px; height: 38px; border-radius: 11px; flex: none; display: flex; align-items: center; justify-content: center;
  background: rgba(102,51,153,0.3); color: #f0d9a3; font-size: 15px; }
.cv-skill-eyebrow { font-size: 10px; letter-spacing: 0.14em; text-transform: uppercase; color: #bcd0ff; }
.cv-skill-title { font-family: 'Cinzel', serif; font-size: 17px; color: #f5e8c0; margin: 1px 0 0; }
.cv-skill-sub { font-size: 12.5px; color: rgba(232,216,255,0.65); margin: 12px 0 14px; }
.cv-polls { display: flex; flex-direction: column; gap: 10px; }
.cv-poll { display: block; width: 100%; background: none; border: none; padding: 4px 6px;
  margin: 0 -6px; border-radius: 8px; cursor: pointer; font-family: inherit; text-align: left;
  transition: background 0.15s; }
.cv-poll:hover { background: rgba(102,51,153,0.18); }
.cv-poll.mine { background: rgba(102,51,153,0.26); }
.cv-poll-top { display: flex; align-items: baseline; justify-content: space-between; margin-bottom: 5px; }
.cv-poll-label { font-size: 12.5px; color: #e9dcff; }
.cv-poll-check { font-size: 10px; color: #f0d9a3; margin-right: 6px; }
.cv-poll.leading .cv-poll-label { color: #f5e8c0; }
.cv-poll-pct { font-size: 11.5px; color: rgba(195,169,255,0.7); }
.cv-poll-bar { display: block; height: 6px; border-radius: 999px; background: rgba(255,255,255,0.07); overflow: hidden; }
.cv-poll-fill { display: block; height: 100%; border-radius: 999px; background: rgba(155,134,201,0.5); }
.cv-poll.leading .cv-poll-fill { background: linear-gradient(90deg,#8a5fd6,#FFDFA3); }
/* Feedback ideas — upvote chip + text. */
.cv-ideas { display: flex; flex-direction: column; gap: 9px; }
.cv-idea { display: flex; align-items: center; gap: 11px; }
.cv-idea-up { flex: none; display: inline-flex; flex-direction: column; align-items: center; line-height: 1;
  min-width: 36px; padding: 5px 0 4px; border-radius: 9px; cursor: pointer; font-family: inherit;
  background: rgba(102,51,153,0.22); border: 1px solid rgba(155,134,201,0.3); color: #c9b8ec;
  transition: background 0.15s, border-color 0.15s, color 0.15s; }
.cv-idea-up:hover { background: rgba(102,51,153,0.4); color: #f0d9a3; }
.cv-idea-up.liked { background: rgba(102,51,153,0.6); border-color: rgba(240,217,163,0.5); color: #f0d9a3; }
.cv-idea-up i { font-size: 12px; }
.cv-idea-up span { font-size: 11px; font-weight: 700; }
.cv-idea-text { font-size: 12.5px; color: #e9dcff; line-height: 1.4; }

.cv-skill-foot { display: flex; align-items: center; justify-content: space-between; margin-top: 16px;
  padding-top: 12px; border-top: 1px solid rgba(255,255,255,0.07); }
.cv-skill-total { font-size: 11px; letter-spacing: 0.06em; text-transform: uppercase; color: rgba(195,169,255,0.6); }
.cv-link { background: none; border: 1px solid rgba(195,169,255,0.35); border-radius: 999px; color: rgba(195,169,255,0.7);
  font-family: inherit; font-size: 11.5px; padding: 5px 13px; cursor: pointer; opacity: 0.7; }
.cv-link-on { display: inline-flex; align-items: center; gap: 6px; border-color: rgba(240,217,163,0.5);
  color: #f0d9a3; opacity: 1; transition: background 0.15s, border-color 0.15s; }
.cv-link-on:hover { background: rgba(102,51,153,0.35); border-color: rgba(240,217,163,0.75); color: #fff7e6; }
</style>
